import React, { useEffect, useMemo } from "react";
import { PlusIcon } from "@heroicons/react/24/outline";
import Select from "components/ui/Select";
import useModalStore from "store/modals";
import { useAllUOMs } from "http-hooks/uom/useUOM";

const SelectUomConversion = ({
  value,
  onChange,
  placeholder = "Search conversion...",
  isDisabled = false,
  isRequired = false,
  label = "Convert",
  className = "",
  filters = {},
  showLabel = true,
  showAddButton = true, // New prop to control plus button visibility
}) => {
  const { openModal } = useModalStore();
  
  const {
    isLoading: isLoadingAllUOMs,
    data: { data: allUOMs } = [],
  } = useAllUOMs({
    itemuom: "",
    itembasicuom: "",
    noPagination: true,
    ...filters,
  });

  // Convert UOMs to options for Select component
  const uomOptions = useMemo(() => {
    return allUOMs?.map(uom => ({
      value: uom.uomconvid,
      label: `${uom.itemuom} = ${uom.conval} ${uom.itembasicuom}`,
      itemuom: uom.itemuom,
      itembasicuom: uom.itembasicuom,
      conval: uom.conval,
      ...uom
    })) || [];
  }, [allUOMs]);

  // Auto-select the matching option when UOMs load and value is provided as simple object
  useEffect(() => {
    // If value is provided as a simple object with itemuom, itembasicuom, conval
    // but not as a full option (missing 'value' or 'label'), find and set the matching option
    if (value && !value.value && value.itemuom && value.itembasicuom && value.conval && uomOptions.length > 0 && onChange) {
      const matchedOption = uomOptions.find(option => 
        option.itemuom === value.itemuom &&
        option.itembasicuom === value.itembasicuom &&
        Number(option.conval) === Number(value.conval)
      );
      
      if (matchedOption && matchedOption !== value) {
        onChange(matchedOption);
      }
    }
  }, [value, uomOptions, onChange]);

  const handleAddNew = () => {
    openModal('uom');
  };

  return (
    <div>
      {showLabel && (
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium leading-6 text-gray-900">
            {label}
          </label>
          {showAddButton && !isDisabled && (
            <button
              type="button"
              onClick={handleAddNew}
              className="ml-2 p-1 text-xs font-bold bg-black text-white rounded hover:bg-gray-800 transition-colors duration-200 flex items-center"
              title="Add new UOM conversion"
            >
              <PlusIcon className="h-3 w-3" aria-hidden="true" />
            </button>
          )}
        </div>
      )}
      <Select
        value={value}
        onChange={onChange}
        options={uomOptions}
        getOptionValue={(option) => option?.value}
        getOptionLabel={(option) => option?.label}
        placeholder={placeholder}
        isDisabled={isDisabled}
        isLoading={isLoadingAllUOMs}
        isRequired={isRequired}
        label={label}
        className={className}
        showLabel={false}
        isClearable={true}
        isSearchable={true}
      />
      {!showLabel && showAddButton && !isDisabled && (
        <button
          type="button"
          onClick={handleAddNew}
          className="absolute top-0 right-0 h-10 w-10 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors duration-200"
          title="Add new UOM conversion"
        >
          <PlusIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      )}
    </div>
  );
};

export default SelectUomConversion; 